// The session-by-id hub — the whole read, including the composition that
// branches between the personal query and the read-only team-member one.
//
// D3 split this file's core out of `src/hooks/query/useQuerySessionById.ts`
// and left the top `useQuerySessionById` composition app-side as THE delegate
// (router / `selectedReviewMemberAtom` / permission-check / workspace reads).
// **Ruling 32 reversed that**, on the programme owner's authorisation of
// 2026-08-27: the composition is here, the old path is a re-export, and the
// app state it branches on arrives through ONE injected config hook. The
// reasoning, the measurement and the reversal consequence are
// `docs/monorepo-ui-floor-rulings/32-the-session-access-context-seam.md`.
//
// Parameterization contract:
// - RENDER-time app state for the two INNER hooks still arrives as params
//   (`isAuthenticated`, `isReviewingOtherUser`, `currentUserId`) — their
//   signatures are stable and their app-side delegates still pass values in.
// - RENDER-time app state for the COMPOSITION arrives from
//   `configureSessionData().useSessionAccessContext(sessionId)` — one required
//   hook member, called unconditionally. See `../config.ts` for why it is
//   required where `useAuthToken` is not.
// - `isAuthenticated` is the PROVIDER-AWARE verdict, not token presence: a
//   persisted token with an unsettled or logged-out provider is not
//   authenticated. It is resolved once by `useSessionAccessContext` and reaches
//   both inner hooks from there, so the composed read gates exactly as the two
//   app-side delegates do.
// - FETCH-time cross-cutting concerns come from `configureSessionData`
//   (`isSessionFetchTimeoutEnabled` thunk · `isSessionNotFoundError`
//   classifier · optional `onSessionFetched` telemetry tag) — see
//   `../config.ts` for why each is a seam member and what heals it.
// Bodies are otherwise byte-identical to the originals.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { logger, sanitizeError } from '@heidi/runtime/logger';
import { type SessionGroupStatus as SchemaSessionGroupStatus } from '@heidi/schema/ml-scribe';

import {
  BackupUploadingStatus,
  FrontendNoteFailureReason,
  GET_SESSION_BY_ID,
  GET_TEAM_MEMBER_SESSION_BY_ID,
  isConsultNoteEmpty,
  isOfflineLocalSession,
  StreamingStatus,
  toSessionGroupStatus,
  type QuerySessionResponse,
  type SelectQuerySessionResponse,
} from '../../contracts';
import { getSessionById, getTeamMemberSessionById } from '../api/session';
import { mapNewBrain } from '../brainUtils';
import { getSessionDataConfig } from '../config';
import { normalizeSessionEhrProvider } from '../normalizeSessionData';
import { notFoundSessionIds } from './notFoundSessionIds';
import { shouldForceSessionRefetch } from './shouldForceSessionRefetch';

// A generation or upload started during the GET, so its cache is newer than the response.
const shouldKeepLiveSessionCache = (
  wasSafeToRefetch: boolean,
  cached: QuerySessionResponse | undefined
): boolean =>
  wasSafeToRefetch && cached !== undefined && !shouldForceSessionRefetch(cached);

// 30s fetch deadline fanned in with TanStack Query's unmount signal.
// `AbortSignal.any`/`.timeout` are polyfilled in `src/polyfills.ts`.
export function signalWithTimeout(
  signal: AbortSignal | undefined,
  timeoutMs: number
): AbortSignal {
  const timeout = AbortSignal.timeout(timeoutMs);
  return signal ? AbortSignal.any([signal, timeout]) : timeout;
}

export function ensureQuerySessionById(sessionId: string) {
  const queryClient = getSessionDataConfig().getQueryClient();
  return queryClient.ensureQueryData({
    queryKey: [GET_SESSION_BY_ID, { sessionId }],
    queryFn: async () => {
      const { data } = await getSessionById(sessionId);
      if (data) {
        const normalizedData = normalizeSessionEhrProvider(data);
        const sessionFromCache = queryClient.getQueryData<QuerySessionResponse>([
          GET_SESSION_BY_ID,
          { sessionId },
        ]);
        return {
          ...normalizedData,
          session_merge_group: normalizedData.session_merge_group ?? { members: [] },
          session_group_status: toSessionGroupStatus(
            normalizedData.session_group_status
          ) as SchemaSessionGroupStatus,
          streamingStatus: StreamingStatus.NOT_STARTED,
          chunkUploadProgress: sessionFromCache?.chunkUploadProgress,
          backupUploadingStatus: BackupUploadingStatus.NOT_STARTED,
          abortController:
            sessionFromCache?.abortController instanceof AbortController
              ? sessionFromCache.abortController
              : new AbortController(),
          isDocumentFromNote: false,
          isAutoGeneratedNoteWarningDismissed: false,
          isSocketError: false,
        };
      }
    },
  });
}

/**
 * Fetches + normalizes a session for the `[GET_SESSION_BY_ID, { sessionId }]`
 * cache key. Shared by {@link useQuerySessionByIdAuthenticated} and
 * {@link usePrefetchSessionById} so a prefetch warms the cache with byte-for-byte
 * the same shape the page reads — including `mapNewBrain` and the cache-preserved
 * flags — and the page's on-mount read is a cache hit rather than a refetch.
 *
 * Signature is unchanged from the original — the flag read, the not-found
 * classifier, and the RUM tag ride the config seam, so the 5 existing direct
 * callers keep working through a pure re-export.
 */
export async function fetchSessionByIdQueryFn({
  sessionId,
  signal,
}: {
  sessionId: string;
  signal?: AbortSignal;
}): Promise<QuerySessionResponse | undefined> {
  const config = getSessionDataConfig();
  // Flag-gated 30s deadline so a hung GET can't pin the page on the skeleton.
  const timedSignal = config.isSessionFetchTimeoutEnabled()
    ? signalWithTimeout(signal, 30_000)
    : signal;
  const queryClient = config.getQueryClient();
  const queryKey = [GET_SESSION_BY_ID, { sessionId }];
  const wasSafeToRefetch = shouldForceSessionRefetch(
    queryClient.getQueryData<QuerySessionResponse>(queryKey)
  );
  try {
    const { data } = await getSessionById(sessionId, timedSignal);
    logger.info('[SessionDetail] Fetched session detail successfully');
    if (data) {
      const normalizedData = normalizeSessionEhrProvider(data);
      config.onSessionFetched?.(sessionId);
      const sessionFromCache = queryClient.getQueryData<QuerySessionResponse>(queryKey);
      if (shouldKeepLiveSessionCache(wasSafeToRefetch, sessionFromCache)) {
        return sessionFromCache;
      }
      normalizedData.consult_note.brain = mapNewBrain(normalizedData.consult_note.brain);
      // A Pro-Actions refusal leaves nothing server-side: keep the client-held
      // reason until a note exists, and null the server one so it cannot mask it.
      const shouldKeepOutOfProActions =
        sessionFromCache?.feError === FrontendNoteFailureReason.OUT_OF_PRO_ACTIONS &&
        isConsultNoteEmpty(normalizedData.consult_note.result);
      // A Stop is client-only: hold it and the restored note body until the server has one.
      const shouldKeepStoppedNote =
        sessionFromCache?.streamingStatus === StreamingStatus.MANUALLY_ABORTED &&
        isConsultNoteEmpty(normalizedData.consult_note.result);

      return {
        ...normalizedData,
        session_merge_group: normalizedData.session_merge_group ?? { members: [] },
        session_group_status: toSessionGroupStatus(
          normalizedData.session_group_status
        ) as SchemaSessionGroupStatus,
        streamingStatus: StreamingStatus.NOT_STARTED,
        chunkUploadProgress: sessionFromCache?.chunkUploadProgress,
        backupUploadingStatus: BackupUploadingStatus.NOT_STARTED,
        abortController:
          sessionFromCache?.abortController instanceof AbortController
            ? sessionFromCache.abortController
            : new AbortController(),
        isDocumentFromNote: sessionFromCache?.isDocumentFromNote ?? false,
        isAutoGeneratedNoteWarningDismissed:
          sessionFromCache?.isAutoGeneratedNoteWarningDismissed ?? false,
        shouldShowNoteSyncChanges: sessionFromCache?.shouldShowNoteSyncChanges ?? false,
        shouldShowDocumentSyncChanges:
          sessionFromCache?.shouldShowDocumentSyncChanges ?? false,
        isSocketError: sessionFromCache?.isSocketError ?? false,
        ...(shouldKeepOutOfProActions && {
          consult_note: { ...normalizedData.consult_note, failure_reason: null },
          feError: FrontendNoteFailureReason.OUT_OF_PRO_ACTIONS,
        }),
        ...(shouldKeepStoppedNote && {
          streamingStatus: StreamingStatus.MANUALLY_ABORTED,
          consult_note: {
            ...normalizedData.consult_note,
            status: sessionFromCache.consult_note.status,
            result: sessionFromCache.consult_note.result,
            content_type: sessionFromCache.consult_note.content_type,
            failure_reason: sessionFromCache.consult_note.failure_reason,
            should_retry: sessionFromCache.consult_note.should_retry,
          },
        }),
      };
    }
  } catch (err) {
    // Mark deleted sessions so downstream queries (permissions, transcript,
    // document, live-transcript) short-circuit via `notFoundSessionIds` —
    // each of those is currently independently 404ing for these users.
    if (config.isSessionNotFoundError(err)) {
      notFoundSessionIds.add(sessionId);
    }
    logger.error('[SessionDetail] Failed to fetch session detail', sanitizeError(err));
    throw err;
  }
}

/**
 * Returns a stable callback that warms the session-by-id cache ahead of
 * navigation. Call it from a session-row click/hover so the GET runs in
 * parallel with the Next route transition instead of serially after the page
 * mounts — the page's `useQuerySessionById` then reads a fresh cache entry
 * (1h `staleTime`) and skips its own fetch.
 *
 * Conservatively no-ops for the whole "reviewing another user" cohort (the
 * `isReviewingOtherUser` param — resolved by the old-path delegate). Without
 * shared-session-edit permission the page reads {@link useQueryTeamMemberSessionById}
 * (a different cache key) and the personal session-by-id endpoint would 403, so
 * warming `GET_SESSION_BY_ID` is wasted/failing work.
 */
export function usePrefetchSessionById({
  isReviewingOtherUser,
}: {
  isReviewingOtherUser: boolean;
}) {
  const queryClient = useQueryClient();
  const { isSessionNotFoundError } = getSessionDataConfig();

  return useCallback(
    (sessionId: string | undefined) => {
      if (!sessionId || isReviewingOtherUser || notFoundSessionIds.has(sessionId)) {
        return;
      }
      void queryClient.prefetchQuery({
        // Same key the page query reads, so this warms the exact entry it consumes.
        queryKey: [GET_SESSION_BY_ID, { sessionId }],
        queryFn: ({ signal }) => fetchSessionByIdQueryFn({ sessionId, signal }),
        // Mirror the page query so a prefetch error settles identically.
        retry: (failureCount, error) =>
          isSessionNotFoundError(error) ? false : failureCount < 1,
        networkMode: 'always',
      });
    },
    [queryClient, isReviewingOtherUser, isSessionNotFoundError]
  );
}

/**
 * THE composed session read — 306 production modules reached it through the old
 * path when it moved here (measured at `origin/main` 45cfd76c4b8f).
 *
 * It branches: a reviewer looking at another member's session WITHOUT
 * shared-edit permission reads the team-member query (a different endpoint and
 * a different cache key), everyone else reads their own. Both hooks are called
 * on every render — the branch picks a result, it does not pick a hook — so the
 * hook order is stable and the unselected query is disabled by its own gate
 * rather than by not existing.
 *
 * The app state it branches on arrives resolved, as one object, from
 * `useSessionAccessContext` (required config member, ruling 32). Nothing in
 * this file reads the router, an atom, a foreign door or the CASL ability.
 */
export function useQuerySessionById<T = QuerySessionResponse>({
  sessionId,
  select,
  refetchOnMount,
}: {
  sessionId: string;
  select?: SelectQuerySessionResponse<T>;
  refetchOnMount?: boolean | 'always';
}) {
  const {
    isExternalLaunch,
    isReviewingOtherUser,
    isReviewingOtherUserCohort,
    teamId,
    memberId,
    hasSharedSessionEditPermission,
    isAuthenticated,
    currentUserId,
  } = getSessionDataConfig().useSessionAccessContext(sessionId);

  const resAuthenticated = useQuerySessionByIdAuthenticated({
    sessionId,
    select,
    isExternalLaunch,
    hasSharedSessionEditPermission,
    // Mount freshness is scoped to the authenticated own/shared-edit query.
    // The read-only team-member delegate below intentionally has no override.
    refetchOnMount,
    isAuthenticated,
    // The COHORT read, not the branch read above. This gate decides whether the
    // personal `GET /sessions/{id}` may fire, and the two expressions diverge
    // while team details are still resolving — passing the branch read here
    // fires that GET for a reviewing user, which 403s and poisons
    // `notFoundSessionIds`. See `SessionAccessContext` for both expressions.
    isReviewingOtherUser: isReviewingOtherUserCohort,
  });
  const memberSession = useQueryTeamMemberSessionById({
    teamId,
    memberId,
    sessionId,
    select,
    isAuthenticated,
    currentUserId,
  });

  if (isReviewingOtherUser && !hasSharedSessionEditPermission) {
    return memberSession;
  }
  return resAuthenticated;
}

export function useQuerySessionByIdAuthenticated<T = QuerySessionResponse | undefined>({
  sessionId,
  select,
  isExternalLaunch = false,
  hasSharedSessionEditPermission = false,
  refetchOnMount,
  isAuthenticated,
  isReviewingOtherUser,
}: {
  sessionId?: string;
  select?: SelectQuerySessionResponse<T>;
  isExternalLaunch?: boolean;
  hasSharedSessionEditPermission?: boolean;
  refetchOnMount?: boolean | 'always';
  /** The settled-or-provisional `isAuthenticated` verdict, delegate-resolved. */
  isAuthenticated: boolean;
  /** The old path's `useIsReviewingOtherUser()` read, delegate-resolved. */
  isReviewingOtherUser: boolean;
}) {
  const config = getSessionDataConfig();

  // Offline local sessions exist only in the RQ cache (put there by
  // useMutateCreateOfflineSession). The server has no record of them, so a
  // fetch would 404, add the ID to notFoundSessionIds, and corrupt the cache.
  const cachedSession = config
    .getQueryClient()
    .getQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }]);

  return useQuery<QuerySessionResponse | undefined, Error, T>({
    enabled:
      isAuthenticated &&
      (!isReviewingOtherUser || hasSharedSessionEditPermission) &&
      !!sessionId &&
      !isExternalLaunch &&
      !isOfflineLocalSession(cachedSession),
    queryKey: [GET_SESSION_BY_ID, { sessionId }],
    select,
    refetchOnMount,
    retry: (failureCount, error) =>
      config.isSessionNotFoundError(error) ? false : failureCount < 1,
    networkMode: 'always',
    // Default-value destructure tolerates direct test calls that omit ctx.
    queryFn: ({ signal }: { signal?: AbortSignal } = {}) => {
      if (!sessionId) return Promise.resolve(undefined);
      return fetchSessionByIdQueryFn({ sessionId, signal });
    },
  });
}

export function useQueryTeamMemberSessionById<T = QuerySessionResponse | undefined>({
  teamId,
  memberId,
  sessionId,
  select,
  isAuthenticated,
  currentUserId,
}: {
  teamId: string;
  memberId: string;
  sessionId: string;
  select?: SelectQuerySessionResponse<T>;
  /** The settled-or-provisional `isAuthenticated` verdict, delegate-resolved. */
  isAuthenticated: boolean;
  /** The old path's `useQueryUser` kinde id read, delegate-resolved. */
  currentUserId: string | undefined;
}) {
  const config = getSessionDataConfig();
  const queryClient = useQueryClient();

  return useQuery<QuerySessionResponse | undefined, Error, T>({
    queryKey: [GET_TEAM_MEMBER_SESSION_BY_ID, { teamId }, { memberId }, { sessionId }],
    staleTime: Infinity,
    enabled:
      isAuthenticated &&
      !!teamId &&
      !!memberId &&
      !!sessionId &&
      memberId !== currentUserId,
    select,
    // See `useQuerySessionByIdAuthenticated`: 404 settles immediately so the
    // session-page can transition to `SessionErrorPage` instead of staying on
    // the skeleton.
    retry: (failureCount, error) =>
      config.isSessionNotFoundError(error) ? false : failureCount < 1,
    // See `useQuerySessionByIdAuthenticated`: bypass the offline-pause path so
    // network failures settle to `'error'` instead of `fetchStatus: 'paused'`.
    networkMode: 'always',
    queryFn: async ({ signal }: { signal?: AbortSignal } = {}) => {
      // See `useQuerySessionByIdAuthenticated`: flag-gated 30s deadline.
      const timedSignal = config.isSessionFetchTimeoutEnabled()
        ? signalWithTimeout(signal, 30_000)
        : signal;
      const queryKey = [
        GET_TEAM_MEMBER_SESSION_BY_ID,
        { teamId },
        { memberId },
        { sessionId },
      ];
      const wasSafeToRefetch = shouldForceSessionRefetch(
        queryClient.getQueryData<QuerySessionResponse>(queryKey)
      );
      try {
        const { data } = await getTeamMemberSessionById({
          teamId,
          memberId,
          sessionId,
          signal: timedSignal,
        });
        logger.info('[SessionDetail] Fetched session detail successfully');
        if (data) {
          const normalizedData = normalizeSessionEhrProvider(data);
          const sessionFromCache =
            queryClient.getQueryData<QuerySessionResponse>(queryKey);
          if (shouldKeepLiveSessionCache(wasSafeToRefetch, sessionFromCache)) {
            return sessionFromCache;
          }
          normalizedData.consult_note.brain = mapNewBrain(
            normalizedData.consult_note.brain
          );

          return {
            ...normalizedData,
            session_merge_group: normalizedData.session_merge_group ?? { members: [] },
            session_group_status: toSessionGroupStatus(
              normalizedData.session_group_status
            ) as SchemaSessionGroupStatus,
            streamingStatus: StreamingStatus.NOT_STARTED,
            chunkUploadProgress: sessionFromCache?.chunkUploadProgress,
            backupUploadingStatus: BackupUploadingStatus.NOT_STARTED,
            abortController: new AbortController(),
            isDocumentFromNote: sessionFromCache?.isDocumentFromNote ?? false,
            isAutoGeneratedNoteWarningDismissed:
              sessionFromCache?.isAutoGeneratedNoteWarningDismissed ?? false,
            shouldShowNoteSyncChanges:
              sessionFromCache?.shouldShowNoteSyncChanges ?? false,
            shouldShowDocumentSyncChanges:
              sessionFromCache?.shouldShowDocumentSyncChanges ?? false,
            isSocketError: false,
          };
        }
      } catch (err) {
        // Mirror `useQuerySessionByIdAuthenticated`: mark deleted sessions so
        // downstream queries short-circuit via `notFoundSessionIds`.
        if (config.isSessionNotFoundError(err)) {
          notFoundSessionIds.add(sessionId);
        }
        logger.error(
          '[SessionDetail] Failed to fetch session detail',
          sanitizeError(err)
        );
        throw err;
      }
    },
  });
}

export function invalidateSessionById(sessionId: string) {
  void getSessionDataConfig()
    .getQueryClient()
    .invalidateQueries({
      queryKey: [GET_SESSION_BY_ID, { sessionId }],
    });
}

export function clearSessionSyncChangesFlag(
  sessionId: string,
  artifactKind: 'note' | 'document'
) {
  getSessionDataConfig()
    .getQueryClient()
    .setQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }], (session) => {
      if (!session) return session;
      return artifactKind === 'note'
        ? { ...session, shouldShowNoteSyncChanges: false }
        : { ...session, shouldShowDocumentSyncChanges: false };
    });
}

/**
 * Linked-sessions cache mapper (moved from `src/hooks/query/sessionByIdKeys.ts`,
 * completing that shim's recorded D3 promise).
 */
export const applyLinkedSessionsCacheUpdate =
  (linkedSessionIds: string[]) =>
  (oldData: QuerySessionResponse | undefined): QuerySessionResponse | undefined => {
    if (!oldData) return undefined;
    return {
      ...oldData,
      linked_sessions_for_context: linkedSessionIds,
      linked_sessions_for_context_fallback: false,
      shouldShowNoteSyncChanges: Boolean(oldData.consult_note?.result),
    };
  };
